/**
 * The playable map at run time and its static collision helpers (DESIGN 4.2).
 * Origin top-left, y down. Every map is a level (level.ts) compiled into an
 * Arena; `ARENA` is the v1 wave arena (40 x 30, four pillars, the pond),
 * level `arena` in arenaLevel.ts. Shared by the server simulation
 * (authoritative movement), client prediction and the ground renderer.
 */

import type { Rect, Vec2 } from './math.js';
import { segRect } from './math.js';
import { WALL_INSET } from './constants.js';
import { compileLevel } from './level.js';
import type { LevelObject } from './level.js';
import { ARENA_LEVEL } from './arenaLevel.js';

export interface Circle { x: number; y: number; r: number }
export interface Ellipse { x: number; y: number; rx: number; ry: number }
/** A moving or temporary round obstacle (other bodies, armored players). */
export interface Collider { pos: Vec2; r: number }
/** A solid circle; `art` names the prop set drawn standing on it (the arena's pillars). */
export interface Pillar extends Circle { art?: string }
/** Standing water: an ellipse (the arena's pond) or a box. */
export type WaterZone = ({ k: 'ellipse' } & Ellipse) | ({ k: 'rect' } & Rect);
/** A named area of the map: the camera, waking enemies and objectives work per room. */
export interface RoomArea { id: string; rect: Rect }
/** A team's base on a PvP map: its spawn ring and its flag's plinth. */
export interface TeamBase { name: string; spawn: Vec2; spawnRing: number; flag: Vec2 }
/** A door: a wall box that can open. Closed doors are also in `blocks`; `art` is the prop drawn while shut. */
export interface DoorArea { id: string; rect: Rect; open: boolean; art?: string }

export interface Arena {
  /** Level id (levels.ts); the `room` frame names it so clients load the same map. */
  id: string;
  w: number;
  h: number;
  /** Solid circles; bodies are pushed out of them. */
  pillars: readonly Pillar[];
  /** The level's permanent wall boxes (blocks without the doors). */
  walls: readonly Rect[];
  /** Solid boxes right now: walls, closed doors and standing brambles. Block bodies, beams, projectiles and sight. */
  blocks: readonly Rect[];
  /** Deep-water cells of the 1 u grid (y * w + x). */
  deepCells: readonly number[];
  /** Bramble cells (they burn away to ash). */
  brambleCells: readonly number[];
  /** Chasm cells (lava and cold fill them with stone). */
  pitCells: readonly number[];
  /** Feet can't cross these right now: open deep water and open chasm. Spells and Teleport cross them. */
  deep: readonly Rect[];
  /** Any body whose centre is inside becomes wet each tick (unless frozen over by Blizzard). */
  water: readonly WaterZone[];
  /** Areas of the map; the arena is one room covering it all. */
  rooms: readonly RoomArea[];
  doors: readonly DoorArea[];
  /** Braziers and rune stones (level.ts LevelObject). */
  objects: readonly LevelObject[];
  /** Enemy spawn gates, cycled in order (DESIGN 4.4). */
  gates: readonly Vec2[];
  /** Centre of the player spawn ring. */
  spawn: Vec2;
  /** Radius of the player spawn ring around `spawn`. */
  spawnRing: number;
  /** Training dummy positions (lobby only). */
  dummies: readonly Vec2[];
  /** Team bases (PvP maps): where each team respawns and where its flag stands. Empty elsewhere. */
  teams: readonly TeamBase[];
  /** The floor image's name in the art manifest, when the level has a single baked plate. */
  plate?: string;
  /** Every floor plate the map is drawn from (one, or several side by side for a big map). */
  plates: readonly string[];
}

/** The v1 wave arena, compiled from its level. */
export const ARENA: Arena = compileLevel(ARENA_LEVEL);

/** True when the point lies inside this water zone (boundary inclusive). */
export function inWaterZone(p: Vec2, z: WaterZone): boolean {
  if (z.k === 'rect') return p.x >= z.x && p.x <= z.x + z.w && p.y >= z.y && p.y <= z.y + z.h;
  const nx = (p.x - z.x) / z.rx;
  const ny = (p.y - z.y) / z.ry;
  return nx * nx + ny * ny <= 1;
}

/** True when the point (an entity centre) stands in any of the map's water (the arena: its pond). */
export function inPond(p: Vec2, arena: Arena = ARENA): boolean {
  for (const z of arena.water) if (inWaterZone(p, z)) return true;
  return false;
}

/**
 * Clamp a body centre inside the walls. DESIGN 4.2 says bodies clamp 0.5 u
 * inside; bigger bodies (troll, jotunn) additionally never overlap the wall,
 * so the inset is max(r, WALL_INSET). Returns a new point.
 */
export function clampToArena(p: Vec2, r: number, arena: Pick<Arena, 'w' | 'h'> = ARENA): Vec2 {
  const inset = Math.max(r, WALL_INSET);
  const maxX = Math.max(inset, arena.w - inset);
  const maxY = Math.max(inset, arena.h - inset);
  return {
    x: p.x < inset ? inset : p.x > maxX ? maxX : p.x,
    y: p.y < inset ? inset : p.y > maxY ? maxY : p.y,
  };
}

/**
 * Where a disc of radius `r` centred at (x, y) goes to stop overlapping box
 * `b`, or null when it does not overlap. From outside it moves straight away
 * from the nearest point of the box; a centre inside leaves by the nearest
 * face (ties: west, east, north, south) that does not open onto the edge of
 * a `w` x `h` map, so a wall along the boundary is left inward rather than
 * through the edge (where the clamp would put the body straight back). With
 * `clear`, a face whose landing spot fails it is passed over for the next
 * nearest, so a body isn't pushed from one wall into another (the nearest face
 * is the fallback when none is clear). Only +, -, *, / and sqrt, so client
 * prediction matches the server bit for bit (movement.ts).
 */
export function pushOutOfRect(x: number, y: number, r: number, b: Rect, w = Infinity, h = Infinity, clear?: (x: number, y: number) => boolean): Vec2 | null {
  const qx = x < b.x ? b.x : x > b.x + b.w ? b.x + b.w : x;
  const qy = y < b.y ? b.y : y > b.y + b.h ? b.y + b.h : y;
  const dx = x - qx, dy = y - qy;
  const d2 = dx * dx + dy * dy;
  if (d2 >= r * r) return null;
  if (d2 > 0) {
    const d = Math.sqrt(d2);
    const push = r - d;
    return { x: x + (dx / d) * push, y: y + (dy / d) * push };
  }
  const west = b.x > 0 ? x - b.x : Infinity;
  const east = b.x + b.w < w ? b.x + b.w - x : Infinity;
  const north = b.y > 0 ? y - b.y : Infinity;
  const south = b.y + b.h < h ? b.y + b.h - y : Infinity;
  const exits: readonly (readonly [number, number, number])[] = [
    [west, b.x - r, y],
    [east, b.x + b.w + r, y],
    [north, x, b.y - r],
    [south, x, b.y + b.h + r],
  ];
  let nearest = exits[0]!;
  let free: readonly [number, number, number] | null = null;
  for (const e of exits) {
    if (e[0] < nearest[0]) nearest = e;
    if (e[0] < Infinity && (!free || e[0] < free[0]) && (!clear || clear(e[1], e[2]))) free = e;
  }
  const out = free ?? nearest;
  return { x: out[1], y: out[2] };
}

/** A landing this close to touching a wall still counts as clear: that is rounding, not an overlap. */
const LANDING_SLACK = 1e-6;

/**
 * Push a body of radius `r` out of every pillar, wall block (and any extra
 * circle colliders), then clamp it inside the walls. Two relaxation passes
 * handle the corner case where one push lands inside another collider.
 * Deterministic: a body exactly centred on a pillar is pushed toward +x.
 */
export function resolveStaticCollision(
  pos: Vec2,
  r: number,
  arena: Arena = ARENA,
  extraCircles: readonly Collider[] = [],
): Vec2 {
  let x = pos.x, y = pos.y;
  const clear = (lx: number, ly: number): boolean => !inBlock({ x: lx, y: ly }, r - LANDING_SLACK, arena);
  for (let pass = 0; pass < 2; pass++) {
    for (const p of arena.pillars) {
      const minD = r + p.r;
      const dx = x - p.x, dy = y - p.y;
      const d2 = dx * dx + dy * dy;
      if (d2 >= minD * minD) continue;
      if (d2 === 0) { x = p.x + minD; continue; }
      const d = Math.sqrt(d2);
      const push = minD - d;
      x += (dx / d) * push;
      y += (dy / d) * push;
    }
    for (const b of arena.blocks) {
      const out = pushOutOfRect(x, y, r, b, arena.w, arena.h, clear);
      if (out) { x = out.x; y = out.y; }
    }
    // deep water: a body knocked in climbs straight back out on the nearest bank
    for (const b of arena.deep) {
      const out = pushOutOfRect(x, y, r, b, arena.w, arena.h, clear);
      if (out) { x = out.x; y = out.y; }
    }
    for (const c of extraCircles) {
      const minD = r + c.r;
      const dx = x - c.pos.x, dy = y - c.pos.y;
      const d2 = dx * dx + dy * dy;
      if (d2 >= minD * minD) continue;
      if (d2 === 0) { x = c.pos.x + minD; continue; }
      const d = Math.sqrt(d2);
      const push = minD - d;
      x += (dx / d) * push;
      y += (dy / d) * push;
    }
    const clamped = clampToArena({ x, y }, r, arena);
    x = clamped.x;
    y = clamped.y;
  }
  return { x, y };
}

/**
 * How far a disc of radius `r` gets going straight from p0 toward p1 before
 * a wall block stops it (Teleport): the stopping point, or p1 itself when
 * nothing is in the way. Blocks are grown by 90% of `r` so a body already
 * resting against a wall can still go away from it; the caller's push-out
 * settles the last bit.
 */
export function clipToBlocks(p0: Vec2, p1: Vec2, r: number, arena: Arena): Vec2 {
  let t = 1;
  for (const b of arena.blocks) {
    const h = segRect(p0, p1, b, r * 0.9);
    if (h && h.t < t) t = h.t;
  }
  return t < 1 ? { x: p0.x + (p1.x - p0.x) * t, y: p0.y + (p1.y - p0.y) * t } : p1;
}

/** Does a disc of radius `r` at p overlap a wall block or deep water, or reach past the map's edge? */
export function inBlock(p: Vec2, r: number, arena: Arena): boolean {
  if (p.x < r || p.y < r || p.x > arena.w - r || p.y > arena.h - r) return true;
  for (const list of [arena.blocks, arena.deep]) {
    for (const b of list) {
      const qx = p.x < b.x ? b.x : p.x > b.x + b.w ? b.x + b.w : p.x;
      const qy = p.y < b.y ? b.y : p.y > b.y + b.h ? b.y + b.h : p.y;
      const dx = p.x - qx, dy = p.y - qy;
      if (dx * dx + dy * dy < r * r) return true;
    }
  }
  return false;
}

/** The 1 u cell index (y * w + x) a point is in, or -1 off the map. */
export function cellAt(arena: Pick<Arena, 'w' | 'h'>, p: Vec2): number {
  const x = Math.floor(p.x), y = Math.floor(p.y);
  if (x < 0 || y < 0 || x >= arena.w || y >= arena.h) return -1;
  return y * arena.w + x;
}
